<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import ConversationApi from 'dashboard/api/inbox/conversation';
import ConversationsApi from 'dashboard/api/conversations';
import MessageApi from 'dashboard/api/inbox/message';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import { dynamicTime } from 'shared/helpers/timeHelper';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  conversation: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['saved', 'close']);

const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const accountLabels = useMapGetter('labels/getLabels');

const dialogRef = ref(null);
const note = ref(props.conversation.kanban_note || '');
const selectedLabels = ref([...(props.conversation.labels || [])]);
const messages = ref([]);
const isLoadingMessages = ref(true);
const isSaving = ref(false);

const contact = computed(() => props.conversation.meta?.sender || {});

const isSelected = title => selectedLabels.value.includes(title);

const toggleLabel = title => {
  selectedLabels.value = isSelected(title)
    ? selectedLabels.value.filter(item => item !== title)
    : [...selectedLabels.value, title];
};

const fetchMessages = async () => {
  try {
    const { data } = await MessageApi.getPreviousMessages({
      conversationId: props.conversation.id,
    });
    // as mais recentes por ultimo; mostramos as 6 finais
    messages.value = (data?.payload || []).slice(-6);
  } catch (error) {
    messages.value = [];
  } finally {
    isLoadingMessages.value = false;
  }
};

const onSave = async () => {
  isSaving.value = true;
  try {
    await ConversationApi.update({
      conversationId: props.conversation.id,
      kanbanNote: note.value,
    });
    const atuais = props.conversation.labels || [];
    const mudou =
      atuais.length !== selectedLabels.value.length ||
      atuais.some(item => !selectedLabels.value.includes(item));
    if (mudou) {
      await ConversationsApi.updateLabels(
        props.conversation.id,
        selectedLabels.value
      );
    }
    emit('saved', {
      kanban_note: note.value,
      labels: [...selectedLabels.value],
    });
  } catch (error) {
    useAlert(t('KANBAN.UPDATE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const openConversation = () => {
  const path = frontendURL(
    conversationUrl({
      accountId: route.params.accountId,
      id: props.conversation.id,
    })
  );
  router.push(path);
};

onMounted(() => {
  dialogRef.value?.open();
  fetchMessages();
});
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="contact.name"
    :confirm-button-label="$t('KANBAN.SAVE')"
    :is-loading="isSaving"
    width="lg"
    overflow-y-auto
    show-cancel-button
    @confirm="onSave"
    @close="emit('close')"
  >
    <div class="flex flex-col gap-5">
      <div class="flex items-center gap-3">
        <Avatar
          :src="contact.thumbnail"
          :name="contact.name || ''"
          :size="40"
          rounded-full
        />
        <div class="min-w-0 flex-1">
          <p class="mb-0 truncate text-sm font-medium text-n-slate-12">
            {{ contact.name }}
          </p>
          <p class="mb-0 truncate text-xs text-n-slate-10">
            {{ contact.phone_number || contact.email || `#${conversation.id}` }}
          </p>
        </div>
        <Button
          color="blue"
          size="sm"
          icon="i-lucide-message-circle"
          :label="$t('KANBAN.ATTEND')"
          @click="openConversation"
        />
      </div>

      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">
          {{ $t('KANBAN.NOTE') }}
        </label>
        <textarea
          v-model="note"
          rows="3"
          class="w-full resize-y rounded-lg border border-n-weak bg-n-alpha-1 p-2 text-sm text-n-slate-12 outline-none focus:border-n-brand"
          :placeholder="$t('KANBAN.NOTE_PLACEHOLDER')"
        />
      </div>

      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">
          {{ $t('KANBAN.LABELS') }}
        </label>
        <div v-if="accountLabels.length" class="flex flex-wrap gap-1.5">
          <button
            v-for="item in accountLabels"
            :key="item.id"
            type="button"
            class="inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs transition-colors"
            :class="
              isSelected(item.title)
                ? 'border-n-brand bg-n-alpha-2 text-n-slate-12'
                : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-1'
            "
            @click="toggleLabel(item.title)"
          >
            <span
              class="size-2 shrink-0 rounded-sm"
              :style="{ backgroundColor: item.color }"
            />
            {{ item.title }}
          </button>
        </div>
        <p v-else class="mb-0 text-xs text-n-slate-10">
          {{ $t('KANBAN.NO_LABELS') }}
        </p>
      </div>

      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">
          {{ $t('KANBAN.LAST_MESSAGES') }}
        </label>
        <div v-if="isLoadingMessages" class="flex justify-center py-4">
          <Spinner />
        </div>
        <div v-else-if="messages.length" class="flex flex-col gap-2">
          <div
            v-for="message in messages"
            :key="message.id"
            class="rounded-lg px-3 py-2 text-sm"
            :class="
              message.message_type === 1
                ? 'ms-6 bg-n-brand/10 text-n-slate-12'
                : 'me-6 bg-n-alpha-2 text-n-slate-12'
            "
          >
            <p class="mb-0 whitespace-pre-line break-words">
              {{ message.content || '—' }}
            </p>
            <span class="text-xxs text-n-slate-10">
              {{ dynamicTime(message.created_at) }}
            </span>
          </div>
        </div>
        <p v-else class="mb-0 text-xs text-n-slate-10">
          {{ $t('KANBAN.NO_MESSAGES') }}
        </p>
      </div>
    </div>
  </Dialog>
</template>
